import React, { useEffect, useState } from 'react';
import { useSiteSettings } from '../../contexts/SiteSettingsContext';
import { instagramApi, InstagramPost } from '../../services/instagramApi';
import styles from './InstagramFeedBlock.module.css';

interface InstagramFeedBlockProps {
  heading?: string;
  showCaptions?: boolean;
  ctaButtonText?: string;
}

export const InstagramFeedBlock: React.FC<InstagramFeedBlockProps> = ({
  heading,
  showCaptions = false,
  ctaButtonText,
}) => {
  const { settings } = useSiteSettings();
  const siteNodeId = settings?.siteNodeId ?? null;

  const [posts, setPosts] = useState<InstagramPost[] | null>(null);
  const [username, setUsername] = useState<string | null>(null);

  useEffect(() => {
    if (!siteNodeId) return;
    let cancelled = false;
    instagramApi.getPosts(siteNodeId)
      .then((res) => {
        if (cancelled) return;
        setPosts(res.posts);
        setUsername(res.username);
      })
      .catch(() => {
        if (cancelled) return;
        setPosts([]);
      });
    return () => { cancelled = true; };
  }, [siteNodeId]);

  if (posts === null) return null;
  if (posts.length === 0) return null;

  const profileUrl = username ? `https://www.instagram.com/${username}/` : undefined;

  return (
    <section className={styles.section}>
      {heading && <h2 className={styles.heading}>{heading}</h2>}
      <div className={styles.grid}>
        {posts.slice(0, 9).map((post) => {
          const src = post.mediaType === 'VIDEO' && post.thumbnailUrl ? post.thumbnailUrl : post.mediaUrl;
          const altText = post.caption?.slice(0, 80) || `Instagram post ${post.id}`;
          return (
            <a
              key={post.id}
              href={post.permalink}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.tile}
              aria-label={altText}
            >
              <img
                src={src}
                alt={altText}
                loading="lazy"
                decoding="async"
                className={styles.image}
              />
              {showCaptions && post.caption && (
                <div className={styles.captionOverlay}>
                  <span>{post.caption}</span>
                </div>
              )}
            </a>
          );
        })}
      </div>
      {ctaButtonText && profileUrl && (
        <div className={styles.ctaWrapper}>
          <a href={profileUrl} target="_blank" rel="noopener noreferrer" className={styles.ctaButton}>
            {ctaButtonText}
          </a>
        </div>
      )}
    </section>
  );
};
